import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE } from '../../config/runtime';

/* ── Inject responsive CSS once — works regardless of Tailwind usage ──────── */
if (typeof document !== 'undefined' && !document.getElementById('mgr-sidebar-css')) {
  const s = document.createElement('style');
  s.id = 'mgr-sidebar-css';
  s.textContent = `
    .mgr-sidebar-desktop {
      display: none;
      flex-direction: column;
      height: 100vh;
      position: sticky;
      top: 0;
      z-index: 50;
      flex-shrink: 0;
      transition: width 0.3s ease;
    }
    .mgr-sidebar-mobile {
      display: flex;
      position: fixed;
      bottom: 0; left: 0; right: 0;
      z-index: 50;
      border-top: 1px solid rgba(255,255,255,0.08);
    }
    @media (min-width: 640px) {
      .mgr-sidebar-desktop { display: flex; }
      .mgr-sidebar-mobile  { display: none; }
    }
  `;
  document.head.appendChild(s);
}

/* ── Icons ─────────────────────────────────────────────────────────────────── */
const DashboardIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
    <rect x="3" y="3" width="7" height="7" rx="1.5" />
    <rect x="14" y="3" width="7" height="4" rx="1.5" />
    <rect x="14" y="10" width="7" height="11" rx="1.5" />
    <rect x="3" y="13" width="7" height="8" rx="1.5" />
  </svg>
);
const CalendarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </svg>
);
const BookingIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
    <path d="M3 8a2 2 0 0 1 2-2h14v4a2 2 0 1 0 0 4v4H5a2 2 0 0 1-2-2z" />
    <path d="M13 6v12" />
  </svg>
);
const ServicesIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
    <path d="M14.7 6.3a1 1 0 0 0-1.4 0l-7 7a1 1 0 0 0 0 1.4l3 3a1 1 0 0 0 1.4 0l7-7a1 1 0 0 0 0-1.4l-3-3z" />
    <path d="M17 3l4 4" />
  </svg>
);
const TherapistIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
    <circle cx="12" cy="8" r="4" />
    <path d="M5 21a7 7 0 0 1 14 0" />
  </svg>
);
const PromoIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
    <path d="M7 7h10a3 3 0 0 1 3 3v1.5a2.5 2.5 0 0 0 0 5V18a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3v-1.5a2.5 2.5 0 0 0 0-5V10a3 3 0 0 1 3-3z" />
    <path d="M12 8v8" />
    <circle cx="12" cy="10" r="1" />
    <circle cx="12" cy="14" r="1" />
  </svg>
);
const NotificationIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
    <path d="M15 17H5.5a1.5 1.5 0 0 1-1.2-2.4l1.2-1.6V10a6.5 6.5 0 1 1 13 0v3l1.2 1.6A1.5 1.5 0 0 1 18.5 17H15" />
    <path d="M9 20a3 3 0 0 0 6 0" />
  </svg>
);
const SupportIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
    <path d="M18 10a6 6 0 1 0-12 0v5a2 2 0 0 0 2 2h2l2 3 2-3h2a2 2 0 0 0 2-2z" />
    <path d="M9 10h6M9 13h4" />
  </svg>
);
const SignOutIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="M16 17l5-5-5-5M21 12H9" />
  </svg>
);
const DotsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="5" cy="12" r="1.5" />
    <circle cx="12" cy="12" r="1.5" />
    <circle cx="19" cy="12" r="1.5" />
  </svg>
);

/* ── Menu config ─────────────────────────────────────────────────────────── */
const MENU = [
  { name: 'Dashboard', short: 'Dash', path: '/manager/dashboard', icon: <DashboardIcon /> },
  { name: 'Calendar', short: 'Cal', path: '/manager/calendar', icon: <CalendarIcon /> },
  { name: 'Booking Request', short: 'Bookings', path: '/manager/booking-request', icon: <BookingIcon /> },
  { name: 'Services', short: 'Services', path: '/manager/services', icon: <ServicesIcon /> },
  { name: 'Therapist', short: 'Staff', path: '/manager/therapist', icon: <TherapistIcon /> },
  { name: 'Promos', short: 'Promos', path: '/manager/promos', icon: <PromoIcon /> },
  { name: 'Notifications', short: 'Alerts', path: '/manager/notifications', icon: <NotificationIcon /> },
  { name: 'Support Center', short: 'Support', path: '/manager/settings', icon: <SupportIcon /> },
];

const BG = 'linear-gradient(160deg, #2a1a15 0%, #1c1310 60%, #150e0b 100%)';

const NotificationBadge = ({ count, compact = false }) => {
  if (count <= 0) return null;
  return (
    <span style={{
      minWidth: compact ? 16 : 18,
      height: compact ? 16 : 18,
      padding: compact ? '0 4px' : '0 5px',
      borderRadius: 999,
      background: '#ef4444',
      color: '#fff',
      fontSize: compact ? 9 : 10,
      fontWeight: 800,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: compact ? 'none' : '0 4px 12px rgba(239,68,68,0.28)',
    }}>
      {compact ? (count > 9 ? '9+' : count) : (count > 99 ? '99+' : count)}
    </span>
  );
};

/* ── ManagerSidebar ──────────────────────────────────────────────────────── */
const ManagerSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);
  const [notificationUnreadCount, setNotificationUnreadCount] = useState(0);

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    let mounted = true;

    const fetchNotificationCount = async () => {
      try {
        const rawUser = localStorage.getItem('user');
        const user = rawUser ? JSON.parse(rawUser) : null;
        if (!user?.user_id) return;

        const response = await fetch(`${API_BASE}/get_notifications.php?user_id=${encodeURIComponent(String(user.user_id))}&limit=10`);
        const data = await response.json();
        if (mounted && data.success) {
          setNotificationUnreadCount(Number(data.unread_count || 0));
        }
      } catch {
        if (mounted) setNotificationUnreadCount(0);
      }
    };

    fetchNotificationCount();
    const intervalId = window.setInterval(fetchNotificationCount, 30000);

    return () => {
      mounted = false;
      window.clearInterval(intervalId);
    };
  }, [location.pathname]);

  const handleSignOut = () => {
    localStorage.removeItem('user');
    navigate('/');
  };

  return (
    <>
      <aside
        className="mgr-sidebar-desktop"
        style={{ width: expanded ? 240 : 72, background: BG }}
        onMouseEnter={() => setExpanded(true)}
        onMouseLeave={() => setExpanded(false)}
      >
        <div style={{
          display: 'flex', flexDirection: 'column', height: '100%',
          margin: 8, borderRadius: 18,
          border: '1px solid rgba(255,255,255,0.07)',
          background: 'rgba(255,255,255,0.04)',
          overflow: 'hidden',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '20px 12px',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
            justifyContent: expanded ? 'flex-start' : 'center',
          }}>
            <div style={{
              flexShrink: 0, width: 36, height: 36, borderRadius: 10,
              background: 'linear-gradient(135deg, #b37b65, #7e4f46)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(179,123,101,0.3)',
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <path d="M12 2C8 2 4 6 4 10c0 5 8 12 8 12s8-7 8-12c0-4-4-8-8-8z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
            </div>
            {expanded && (
              <div style={{ overflow: 'hidden' }}>
                <p style={{ margin: 0, fontSize: 10, fontWeight: 800, letterSpacing: '0.22em', color: '#b37b65', textTransform: 'uppercase', lineHeight: 1 }}>Hygea</p>
                <p style={{ margin: '2px 0 0', fontSize: 13, fontWeight: 900, color: '#fff', lineHeight: 1, whiteSpace: 'nowrap' }}>Branch Manager</p>
              </div>
            )}
          </div>

          <nav style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '10px 8px', display: 'flex', flexDirection: 'column', gap: 2 }}>
            {expanded && (
              <p style={{ margin: '0 0 6px 6px', fontSize: 9, fontWeight: 800, color: 'rgba(255,255,255,0.2)', textTransform: 'uppercase', letterSpacing: '0.22em' }}>Menu</p>
            )}
            {MENU.map((item) => {
              const active = isActive(item.path);
              const showNotificationBadge = item.path === '/manager/notifications' && notificationUnreadCount > 0;
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  title={!expanded ? item.name : ''}
                  style={{
                    position: 'relative',
                    display: 'flex', alignItems: 'center', gap: 12,
                    width: '100%', padding: 10, borderRadius: 12,
                    border: 'none', cursor: 'pointer',
                    justifyContent: expanded ? 'flex-start' : 'center',
                    background: active ? 'linear-gradient(90deg, rgba(179,123,101,0.22), rgba(179,123,101,0.08))' : 'transparent',
                    color: active ? '#fff' : 'rgba(255,255,255,0.4)',
                    transition: 'background 0.15s, color 0.15s',
                  }}
                  onMouseEnter={(e) => { if (!active) { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = 'rgba(255,255,255,0.8)'; } }}
                  onMouseLeave={(e) => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; } }}
                >
                  {active && (
                    <span style={{
                      position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)',
                      width: 3, height: 20, borderRadius: 2, background: '#b37b65',
                    }} />
                  )}
                  <span style={{ flexShrink: 0, color: active ? '#c8896e' : 'inherit', display: 'flex' }}>
                    {item.icon}
                  </span>
                  {showNotificationBadge && <NotificationBadge count={notificationUnreadCount} />}
                  {expanded && (
                    <span style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', letterSpacing: '0.02em' }}>
                      {item.name}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          <div style={{ padding: 8, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <button
              onClick={handleSignOut}
              title={!expanded ? 'Sign Out' : ''}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                width: '100%', padding: 10, borderRadius: 12,
                border: 'none', cursor: 'pointer',
                background: 'transparent', color: 'rgba(255,255,255,0.35)',
                justifyContent: expanded ? 'flex-start' : 'center',
                transition: 'background 0.15s, color 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; e.currentTarget.style.color = '#f87171'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.35)'; }}
            >
              <span style={{ flexShrink: 0, display: 'flex' }}><SignOutIcon /></span>
              {expanded && <span style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>Sign Out</span>}
            </button>
          </div>
        </div>
      </aside>

      <nav
        className="mgr-sidebar-mobile"
        style={{ background: 'linear-gradient(0deg, #1c1310 0%, #2a1a15 100%)' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', width: '100%', padding: '6px 4px 8px' }}>
          {MENU.slice(0, 5).map((item) => {
            const active = isActive(item.path);
            const showNotificationBadge = item.path === '/manager/notifications' && notificationUnreadCount > 0;
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                  padding: '6px 8px', borderRadius: 12, border: 'none', cursor: 'pointer',
                  background: 'transparent',
                  color: active ? '#c8896e' : 'rgba(255,255,255,0.3)',
                  minWidth: 52, transition: 'color 0.15s',
                }}
              >
                <span style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 32, height: 28, borderRadius: 8,
                  background: active ? 'rgba(179,123,101,0.2)' : 'transparent',
                  transition: 'background 0.15s',
                  position: 'relative',
                }}>
                  {item.icon}
                  {showNotificationBadge && (
                    <span style={{ position: 'absolute', top: -2, right: -5 }}>
                      <NotificationBadge count={notificationUnreadCount} compact />
                    </span>
                  )}
                </span>
                <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.04em', lineHeight: 1, color: active ? '#c8896e' : 'rgba(255,255,255,0.28)' }}>
                  {item.short}
                </span>
              </button>
            );
          })}
          <MobileMore items={MENU.slice(5)} isActive={isActive} navigate={navigate} handleSignOut={handleSignOut} notificationUnreadCount={notificationUnreadCount} />
        </div>
      </nav>
    </>
  );
};

/* ── Mobile More popover ─────────────────────────────────────────────────── */
const MobileMore = ({ items, isActive, navigate, handleSignOut, notificationUnreadCount }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      {open && <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setOpen(false)} />}
      {open && (
        <div style={{
          position: 'absolute', bottom: 60, right: 0, zIndex: 50,
          minWidth: 180, borderRadius: 16, overflow: 'hidden',
          background: '#1c1310',
          border: '1px solid rgba(255,255,255,0.1)',
          boxShadow: '0 -8px 32px rgba(0,0,0,0.4)',
        }}>
          {items.map((item) => {
            const active = isActive(item.path);
            const showNotificationBadge = item.path === '/manager/notifications' && notificationUnreadCount > 0;
            return (
              <button
                key={item.path}
                onClick={() => { navigate(item.path); setOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  width: '100%', padding: '12px 16px', border: 'none', cursor: 'pointer',
                  background: active ? 'rgba(179,123,101,0.15)' : 'transparent',
                  color: active ? '#c8896e' : 'rgba(255,255,255,0.55)',
                  fontSize: 13, fontWeight: 600, transition: 'background 0.1s',
                }}
                onMouseEnter={(e) => { if (!active) { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; e.currentTarget.style.color = '#fff'; } }}
                onMouseLeave={(e) => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.55)'; } }}
              >
                <span style={{ flexShrink: 0, display: 'flex', position: 'relative' }}>
                  {item.icon}
                  {showNotificationBadge && (
                    <span style={{ position: 'absolute', top: -6, right: -10 }}>
                      <NotificationBadge count={notificationUnreadCount} compact />
                    </span>
                  )}
                </span>
                <span style={{ whiteSpace: 'nowrap' }}>{item.name}</span>
              </button>
            );
          })}
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
            <button
              onClick={() => { handleSignOut(); setOpen(false); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                width: '100%', padding: '12px 16px', border: 'none', cursor: 'pointer',
                background: 'transparent', color: 'rgba(255,255,255,0.35)',
                fontSize: 13, fontWeight: 600, transition: 'background 0.1s, color 0.1s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; e.currentTarget.style.color = '#f87171'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.35)'; }}
            >
              <span style={{ display: 'flex' }}><SignOutIcon /></span>
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      )}
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
          padding: '6px 8px', borderRadius: 12, border: 'none', cursor: 'pointer',
          background: 'transparent', color: open ? '#c8896e' : 'rgba(255,255,255,0.3)',
          minWidth: 52,
        }}
      >
        <span style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 32, height: 28, borderRadius: 8,
          background: open ? 'rgba(179,123,101,0.2)' : 'transparent',
        }}>
          <DotsIcon />
        </span>
        <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.04em', lineHeight: 1, color: open ? '#c8896e' : 'rgba(255,255,255,0.28)' }}>
          More
        </span>
      </button>
    </div>
  );
};

export default ManagerSidebar;
